import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Download, Loader2, Lock, LogOut, RefreshCw, Search, StickyNote, Trash2 } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, BarChart, Bar } from "recharts";
import axios from "axios";
import Offers from "@/components/Offers";
import PremiumPasses from "@/components/PremiumPasses";
import AdminMenu from "@/components/AdminMenu";
import ContactMessages from "@/components/ContactMessages";
import EditorialChecks from "@/components/EditorialChecks";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = "astra_analytics_token";
const GOLD = "#D4AF37";
const card = "bg-[#0F1523] border border-[#D4AF37]/20 rounded-2xl p-5";

export function Login({ onToken }) {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await axios.post(`${API}/analytics/login`, { password: pw });
      sessionStorage.setItem(KEY, data.token);
      onToken(data.token);
    } catch {
      setErr("That password isn't right.");
    }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className={`${card} max-w-sm mx-auto mt-24`} data-testid="analytics-login">
      <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] gold-text mb-2"><Lock size={13} /> Private</p>
      <h1 className="text-3xl font-light mb-4">Astra analytics</h1>
      <input data-testid="analytics-password" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password" autoFocus
        className="w-full bg-[#0B0F19] border border-[#D4AF37]/25 rounded-xl px-4 py-3 text-starlight outline-none focus:border-[#D4AF37]" />
      {err && <p data-testid="analytics-login-error" className="text-sm text-red-300 mt-2">{err}</p>}
      <button data-testid="analytics-login-btn" disabled={busy || !pw} className="w-full mt-4 bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] font-medium py-3 rounded-full disabled:opacity-60">
        {busy ? <Loader2 size={16} className="animate-spin mx-auto" /> : "View dashboard"}
      </button>
    </form>
  );
}

function Stat({ label, value, sub, testId }) {
  return (
    <div className={card} data-testid={testId}>
      <p className="text-[11px] uppercase tracking-[0.2em] text-[#A7B0C0]">{label}</p>
      <p className="text-3xl font-light mt-1 gold-text">{value}</p>
      {sub && <p className="text-xs text-[#A7B0C0] mt-1">{sub}</p>}
    </div>
  );
}

function Group({ title, range, children }) {
  return (
    <section className="mb-10">
      <h2 className="text-lg text-starlight mb-3 flex items-baseline gap-2">
        {title}
        {range && <span data-testid={`range-label-${title.toLowerCase()}`} className="text-xs text-[#A7B0C0] font-normal">{range}</span>}
      </h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{children}</div>
    </section>
  );
}

function Chart({ title, data, dataKey = "n", testId, bars = false }) {
  const rows = data.map((d) => ({ ...d, day: d._id.slice(5) }));
  const Kind = bars ? BarChart : AreaChart;
  return (
    <div className={`${card} col-span-2 lg:col-span-4`} data-testid={testId}>
      <p className="text-sm text-[#A7B0C0] mb-3">{title}</p>
      {rows.length === 0 ? <p className="text-sm text-[#A7B0C0] py-8 text-center">No data yet</p> : (
        <ResponsiveContainer width="100%" height={180}>
          <Kind data={rows} margin={{ left: -24, right: 8 }}>
            <XAxis dataKey="day" stroke="#5f6b7d" fontSize={11} />
            <YAxis allowDecimals={false} stroke="#5f6b7d" fontSize={11} />
            <Tooltip contentStyle={{ background: "#0B0F19", border: "1px solid #D4AF3755", borderRadius: 12 }} />
            {bars ? <Bar dataKey={dataKey} fill={GOLD} radius={[4, 4, 0, 0]} />
              : <Area dataKey={dataKey} stroke={GOLD} fill={GOLD} fillOpacity={0.18} strokeWidth={2} />}
          </Kind>
        </ResponsiveContainer>
      )}
    </div>
  );
}

function List({ title, rows, testId, fmt = (r) => r._id }) {
  const max = Math.max(1, ...rows.map((r) => r.n));
  return (
    <div className={`${card} col-span-2`} data-testid={testId}>
      <p className="text-sm text-[#A7B0C0] mb-3">{title}</p>
      {rows.length === 0 && <p className="text-sm text-[#A7B0C0]">No data yet</p>}
      <ul className="space-y-2">
        {rows.map((r) => (
          <li key={String(r._id)} className="text-sm">
            <div className="flex justify-between"><span className="text-starlight truncate pr-2">{fmt(r) || "Unknown"}</span><span className="text-[#A7B0C0]">{r.n}</span></div>
            <div className="h-1 mt-1 rounded-full bg-[#D4AF37]/10"><div className="h-1 rounded-full bg-[#D4AF37]" style={{ width: `${(r.n / max) * 100}%` }} /></div>
          </li>
        ))}
      </ul>
    </div>
  );
}

const regionNames = new Intl.DisplayNames(["en-GB"], { type: "region" });
const flag = (cc) => {
  if (!cc || cc.length !== 2 || cc === "??") return "Unknown";
  try { return regionNames.of(cc.toUpperCase()) || cc; } catch { return cc; }
};
const mins = (s) => (s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`);

const RANGES = [
  { key: "24h", label: "Today", q: "hours=24", text: "Last 24 hours" },
  { key: "7", label: "7d", q: "days=7", text: "Last 7 days" },
  { key: "30", label: "30d", q: "days=30", text: "Last 30 days" },
  { key: "90", label: "90d", q: "days=90", text: "Last 90 days" },
];

function downloadCsv(rows) {
  const head = ["Name", "Email", "Plan", "Since", "Renews / ends", "Renewals", "Paid", "Currency", "Status"];
  const day = (iso) => (iso ? iso.slice(0, 10) : "");
  const cell = (v) => {
    let t = String(v ?? "");
    if (/^[=+\-@]/.test(t)) t = `'${t}`;
    return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
  };
  const lines = [head, ...rows.map((r) => [r.name, r.email, r.plan, day(r.since), r.until ? day(r.until) : "No end date",
    r.renewals, r.paid ? r.paid.toFixed(2) : "0", r.currency, r.active ? "Active" : "Ended"])].map((l) => l.map(cell).join(","));
  const blob = new Blob(["\ufeff" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `astra-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

function NotesPanel({ userId, token, onCount }) {
  const [notes, setNotes] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const headers = { "X-Analytics-Token": token };
  useEffect(() => {
    axios.get(`${API}/analytics/notes/${userId}`, { headers }).then((r) => setNotes(r.data)).catch(() => setNotes([]));
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps
  const update = (list) => { setNotes(list); onCount(list.length); };
  const add = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await axios.post(`${API}/analytics/notes/${userId}`, { text }, { headers });
      update([data, ...notes]); setText("");
    } catch (er) { setErr(er.response?.data?.detail || "Couldn't save the note."); }
    setBusy(false);
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this note?")) return;
    await axios.delete(`${API}/analytics/notes/${userId}/${id}`, { headers });
    update(notes.filter((n) => n.id !== id));
  };
  const when = (iso) => new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  return (
    <div className="px-4 pb-4 pt-1" data-testid={`notes-panel-${userId}`}>
      <form onSubmit={add} className="flex gap-2 mb-3">
        <textarea data-testid="note-input" value={text} onChange={(e) => setText(e.target.value)} rows={2} maxLength={2000}
          placeholder="Add a private note, e.g. 'Emailed about refund on 3 Nov'"
          className="flex-1 bg-[#0B0F19] border border-[#D4AF37]/25 rounded-xl px-3 py-2 text-sm text-starlight outline-none focus:border-[#D4AF37] resize-y" />
        <button data-testid="note-save-btn" disabled={busy || !text.trim()}
          className="self-end bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] text-sm px-4 py-2 rounded-full disabled:opacity-50 transition-colors">
          {busy ? <Loader2 size={14} className="animate-spin" /> : "Save note"}
        </button>
      </form>
      {err && <p data-testid="note-error" className="text-xs text-red-300 mb-2">{err}</p>}
      {notes === null ? <Loader2 size={16} className="animate-spin gold-text" /> : notes.length === 0 ? (
        <p className="text-xs text-[#A7B0C0]" data-testid="notes-empty">No notes yet.</p>
      ) : (
        <ul className="space-y-2">
          {notes.map((n) => (
            <li key={n.id} data-testid="note-item" className="flex gap-3 items-start border-l-2 border-[#D4AF37]/50 pl-3">
              <div className="flex-1">
                <p className="text-[11px] text-[#A7B0C0]">{when(n.created_at)}</p>
                <p className="text-sm text-starlight whitespace-pre-wrap break-words">{n.text}</p>
              </div>
              <button data-testid={`note-delete-${n.id}`} onClick={() => remove(n.id)} aria-label="Delete note"
                className="p-1 text-[#A7B0C0] hover:text-red-300 transition-colors"><Trash2 size={14} /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function NoteSearch({ token, onPick }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    const t = setTimeout(() => {
      axios.get(`${API}/analytics/notes-search`, { params: { q }, headers: { "X-Analytics-Token": token } })
        .then((r) => setRes(r.data)).catch(() => setRes([]));
    }, 300);
    return () => clearTimeout(t);
  }, [q, token]);
  const day = (iso) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  return (
    <div className="mb-3" data-testid="note-search">
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A7B0C0]" />
        <input data-testid="note-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search all subscriber notes"
          className="w-full bg-[#0F1523] border border-[#D4AF37]/20 rounded-full pl-9 pr-4 py-2.5 text-sm text-starlight outline-none focus:border-[#D4AF37]" />
      </div>
      {res && (
        <div className="mt-2 bg-[#0F1523] border border-[#D4AF37]/20 rounded-2xl p-2" data-testid="note-search-results">
          {res.length === 0 ? <p className="p-3 text-sm text-[#A7B0C0]" data-testid="note-search-empty">No notes match "{q}"</p> : res.map((n) => (
            <button key={n.id} data-testid={`note-search-result-${n.id}`} onClick={() => onPick(n.user_id)}
              className="w-full text-left p-3 rounded-xl hover:bg-[#D4AF37]/10 transition-colors">
              <p className="text-xs text-[#A7B0C0]"><span className="gold-text">{n.name || "Unknown member"}</span> · {n.email} · {day(n.created_at)}</p>
              <p className="text-sm text-starlight line-clamp-2 mt-0.5">{n.text}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Subscribers({ rows, token }) {
  const [open, setOpen] = useState(null);
  const [counts, setCounts] = useState({});
  const pick = (id) => {
    setOpen(id);
    setTimeout(() => document.querySelector(`[data-testid="notes-panel-${id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
  };
  const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "No end date");
  return (
    <section className="mb-10" data-testid="subscribers-section">
      <h2 className="text-lg text-starlight mb-3 flex items-baseline gap-2">
        Subscribers <span className="text-xs text-[#A7B0C0]">{rows.filter((r) => r.active).length} active · all time</span>
        {rows.length > 0 && (
          <button data-testid="btn-download-csv" onClick={() => downloadCsv(rows)}
            className="ml-auto flex items-center gap-1.5 text-sm border border-[#D4AF37]/40 gold-text px-3 py-1.5 rounded-full hover:border-[#D4AF37] transition-colors">
            <Download size={14} /> Download CSV
          </button>
        )}
      </h2>
      {rows.length > 0 && <NoteSearch token={token} onPick={pick} />}
      <div className="bg-[#0F1523] border border-[#D4AF37]/20 rounded-2xl overflow-x-auto">
        {rows.length === 0 ? <p className="p-5 text-sm text-[#A7B0C0]">No subscribers yet</p> : (
          <table className="w-full text-sm" data-testid="subscribers-table">
            <thead className="text-[11px] uppercase tracking-[0.15em] text-[#A7B0C0]">
              <tr className="border-b border-[#D4AF37]/15">
                {["Name", "Email", "Plan", "Since", "Renews / ends", "Renewals", "Paid", "Status", "Notes"].map((h) => (
                  <th key={h} className="text-left font-normal px-4 py-3 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const n = counts[r.id] ?? r.notes ?? 0;
                return (
                <Fragment key={r.id}>
                <tr data-testid="subscriber-row" className={`border-b border-[#D4AF37]/10 ${open === r.id ? "bg-[#D4AF37]/5" : ""}`}>
                  <td className="px-4 py-3 whitespace-nowrap text-starlight">{r.name}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-[#D6D0C5]">{r.email}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{r.plan}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{fmt(r.since)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{fmt(r.until)}</td>
                  <td className="px-4 py-3">{r.renewals}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{r.paid ? `${r.paid.toFixed(2)} ${r.currency}` : "Free"}</td>
                  <td className="px-4 py-3"><span className={r.active ? "gold-text" : "text-[#A7B0C0]"}>{r.active ? "Active" : "Ended"}</span></td>
                  <td className="px-4 py-3">
                    <button data-testid={`btn-notes-${r.id}`} onClick={() => setOpen(open === r.id ? null : r.id)}
                      className={`flex items-center gap-1.5 whitespace-nowrap text-xs px-2.5 py-1 rounded-full border transition-colors ${n ? "border-[#D4AF37]/60 gold-text" : "border-[#D4AF37]/20 text-[#A7B0C0]"} hover:border-[#D4AF37]`}>
                      <StickyNote size={12} /> {n || "Add"}
                    </button>
                  </td>
                </tr>
                {open === r.id && (
                  <tr className="border-b border-[#D4AF37]/10 bg-[#D4AF37]/5">
                    <td colSpan={9}><NotesPanel userId={r.id} token={token} onCount={(c) => setCounts((s) => ({ ...s, [r.id]: c }))} /></td>
                  </tr>
                )}
                </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

function Dashboard({ d, range, token }) {
  const { users: u, money: m, engagement: e, activity: a } = d;
  return (
    <>
      <Group title="Activity" range={range.text}>
        <Stat label="Unique visitors" value={a.unique_visitors} sub={`${a.visitors_today} today`} testId="stat-visitors" />
        <Stat label="Page views" value={a.page_views} sub={`${a.visits} visits`} testId="stat-pageviews" />
        <Stat label="Avg visit" value={mins(a.avg_visit_seconds)} testId="stat-duration" />
        <Stat label="Pages / visit" value={a.visits ? (a.page_views / a.visits).toFixed(1) : "0"} testId="stat-ppv" />
        <Chart title="Visitors by day" data={a.visitors_by_day} testId="chart-visitors" />
        <List title="Sources" rows={a.sources} testId="list-sources" />
        <List title="Countries" rows={a.countries} testId="list-countries" fmt={(r) => flag(r._id)} />
        <List title="Devices" rows={a.devices} testId="list-devices" />
        <List title="Operating systems" rows={a.os} testId="list-os" />
        <List title="Top pages" rows={a.top_pages} testId="list-pages" />
      </Group>
      <Group title="Users" range={`${range.text} (sign-up chart) · totals all time`}>
        <Stat label="Total sign-ups" value={u.total} testId="stat-users-total" />
        <Stat label="New today" value={u.new_today} testId="stat-users-today" />
        <Stat label="New this week" value={u.new_week} testId="stat-users-week" />
        <Stat label="Active today" value={u.active_today} sub="signed-in users" testId="stat-users-active" />
        <Chart title="Sign-ups by day" data={u.signups_by_day} bars testId="chart-signups" />
      </Group>
      <Group title="Money" range={`${range.text} (packs & revenue) · members now`}>
        <Stat label="Premium members" value={m.premium_members} testId="stat-premium" />
        <Stat label="Question packs sold" value={m.packs_sold} testId="stat-packs" />
        {m.revenue.length === 0 && <Stat label="Revenue" value="0" testId="stat-revenue-none" />}
        {m.revenue.map((r) => (
          <Stat key={r.currency} label={`Revenue ${r.currency}`} value={r.total.toFixed(2)} sub={`${r.count} payments`} testId={`stat-revenue-${r.currency}`} />
        ))}
      </Group>
      <Group title="Engagement" range={`${range.text} (readings & questions) · rest now`}>
        <Stat label="Readings opened" value={e.readings_opened} sub={`${e.readings_today} today`} testId="stat-readings" />
        <Stat label="Astra questions" value={e.questions} sub={`+ ${e.guest_questions} from guests`} testId="stat-questions" />
        <Stat label="Active streaks" value={e.streaks_active} sub={`avg ${e.streak_avg} · longest ${e.streak_max}`} testId="stat-streaks" />
        <Stat label="Badges earned" value={`${e.badges["7"]} · ${e.badges["30"]} · ${e.badges["100"]}`} sub="7 · 30 · 100 days" testId="stat-badges" />
        <Stat label="Friends invited" value={e.invites} testId="stat-invites" />
        <Stat label="Push reminders on" value={e.push_on} testId="stat-push" />
        <Stat label="Email reminders on" value={e.email_on} testId="stat-email" />
        <Stat label="Win-back emails" value={d.winback.sent} sub={`${d.winback.waiting} still within 7 days`} testId="stat-winback-sent" />
        <Stat label="Came back" value={d.winback.came_back}
          sub={d.winback.sent ? `${Math.round((d.winback.came_back / d.winback.sent) * 100)}% opened a reading within 7 days` : "opened a reading within 7 days"} testId="stat-winback-back" />
        <List title="Invite leaderboard" rows={e.leaderboard.map((l) => ({ _id: l.name, n: l.count }))} testId="list-leaderboard" />
      </Group>
      <Offers token={token} />
      <PremiumPasses token={token} />
      <div className="mt-8"><ContactMessages token={token} /></div>
      <div className="mt-8"><EditorialChecks token={token} /></div>
      <Subscribers rows={d.subscribers} token={token} />
    </>
  );
}

export default function Analytics() {
  const [token, setToken] = useState(() => sessionStorage.getItem(KEY));
  const [rk, setRk] = useState("30");
  const range = RANGES.find((r) => r.key === rk);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);

  const latest = useRef(0);
  const load = useCallback(async () => {
    if (!token) return;
    const req = ++latest.current;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/analytics/summary?${range.q}`, { headers: { "X-Analytics-Token": token } });
      if (req === latest.current) setD(data);
    } catch (err) {
      if (err.response?.status === 401) { sessionStorage.removeItem(KEY); setToken(null); }
    }
    setLoading(false);
  }, [token, range.q]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots"; m.content = "noindex,nofollow";
    document.head.appendChild(m);
    return () => m.remove();
  }, []);

  const signOut = () => { sessionStorage.removeItem(KEY); setToken(null); setD(null); };

  return (
    <div className="min-h-screen bg-[#04060B] text-starlight px-5 pt-16 pb-8" data-testid="analytics-page">
      <AdminMenu />
      {!token ? <Login onToken={setToken} /> : (
        <div className="max-w-5xl mx-auto">
          <header className="flex flex-wrap items-center gap-3 mb-8">
            <div>
              <p className="text-[11px] uppercase tracking-[0.3em] gold-text">Astra · Private</p>
              <h1 className="text-3xl font-light">Analytics</h1>
            </div>
            <div className="ml-auto flex items-center gap-2">
              {RANGES.map((r) => (
                <button key={r.key} data-testid={`range-${r.key}`} onClick={() => setRk(r.key)}
                  className={`px-3 py-1.5 rounded-full text-sm ${rk === r.key ? "bg-[#D4AF37] text-[#04060B]" : "border border-[#D4AF37]/30 text-[#A7B0C0]"}`}>
                  {r.label}
                </button>
              ))}
              <button data-testid="analytics-refresh" onClick={load} aria-label="Refresh" className="p-2 text-[#A7B0C0] hover:text-starlight">
                <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              </button>
              <button data-testid="analytics-signout" onClick={signOut} aria-label="Sign out" className="p-2 text-[#A7B0C0] hover:text-starlight"><LogOut size={16} /></button>
            </div>
          </header>
          {!d ? <Loader2 className="animate-spin gold-text mx-auto mt-24" size={28} /> : <Dashboard d={d} range={range} token={token} />}
          <p className="text-xs text-[#5f6b7d] mt-4">Visitor stats come from Astra's own anonymous tracking (from 19 Oct 2026). Ranges that reach back before then show the same totals.</p>
        </div>
      )}
    </div>
  );
}
